import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import PlayerCard from './PlayerCard';
import { Player } from '../types';
import { warmupSounds } from '../Utils/soundUtil';

const AnimatedPlayerCard = styled(motion.div)`
  width: 100%;
  display: flex;
  justify-content: center;
  /* reserve space under the last card so it doesn't get covered by fixed footer */
  &:last-child {
    margin-bottom: 64px; /* footer 40px + some breathing room */
  }
`;

interface GameBoardProps {
  players: Player[];
  onDiceValueChange: (diceIndex: number, value: number) => void;
  endTurn: () => void;
  toggleFreeze: (diceIndex: number) => void;
  soundEnabled: boolean;
  botDifficulty?: 'easy' | 'medium' | 'hard';
}

const GameBoard: React.FC<GameBoardProps> = ({
  players,
  onDiceValueChange,
  endTurn,
  toggleFreeze,
  soundEnabled,
  botDifficulty,
}) => {
  // Track the current player to detect turn changes
  const currentPlayerIdRef = useRef<string | null>(null);
  const [shouldAnimate, setShouldAnimate] = React.useState(false);

  // Covers restoring straight into /game (page refresh) without visiting the lobby
  useEffect(() => {
    warmupSounds();
  }, []);

  // Sort players so current player is always first
  const sortedPlayers = [...players].sort((a, b) => {
    if (a.isCurrent && !b.isCurrent) return -1;
    if (!a.isCurrent && b.isCurrent) return 1;
    return 0;
  });

  // Detect when the current player changes (turn ends)
  useEffect(() => {
    const currentPlayer = players.find(p => p.isCurrent);
    const newCurrentPlayerId = currentPlayer?.id || null;
    
    if (currentPlayerIdRef.current !== null && currentPlayerIdRef.current !== newCurrentPlayerId) {
      // Turn has changed, trigger animation
      setShouldAnimate(true);
      setTimeout(() => setShouldAnimate(false), 600); // Reset after animation completes
    }
    
    currentPlayerIdRef.current = newCurrentPlayerId;
  }, [players]);

  return (
    <>
      <AnimatePresence>
        {sortedPlayers.map((player, index) => (
          <AnimatedPlayerCard
            key={player.id}
            layout={shouldAnimate}
            initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
            animate={{ 
              opacity: 1, 
              y: 0,
              scale: player.isCurrent ? 1.02 : 1,
              zIndex: player.isCurrent ? 10 : 1
            }}
            exit={shouldAnimate ? { opacity: 0, y: -20 } : undefined}
            transition={{ 
              duration: shouldAnimate ? 0.4 : 0.1, 
              ease: "easeInOut",
              layout: shouldAnimate ? { duration: 0.5, ease: "easeInOut" } : { duration: 0 }
            }}
          >
            <PlayerCard
              player={player}
              onDiceValueChange={onDiceValueChange}
              endTurn={endTurn}
              toggleFreeze={toggleFreeze}
              soundEnabled={soundEnabled}
              botDifficulty={player.id === 'bot' ? botDifficulty : undefined}
            />
          </AnimatedPlayerCard>
        ))}
      </AnimatePresence>
    </>
  );
};

export default GameBoard;